import { Box, CircularProgress } from "@mui/material";
import {
  PictureAsPdfOutlined as PdfIcon,
  UploadFileOutlined as UploadIcon,
  OpenInNewOutlined as OpenIcon,
  DeleteOutlineOutlined as DeleteIcon,
} from "@mui/icons-material";
import { useRef, useState } from "react";
import { v2 } from "../ui/v2Tokens";
import { uploadDocument } from "../../../../services/MediaUploadService";
import { Card, EmptyState, TextButton } from "../ui/primitives";

type Props = {
  pdfUrl?: string;
  pdfName?: string;
  onChange: (file: { pdfUrl?: string; pdfName?: string }) => void;
};

const MAX_MB = 20;

/**
 * The menu file itself, for a trainer who already writes menus outside the app. The
 * trainee opens this in the mobile app; the calculated targets ride along as the daily
 * summary, so the menu is still measurable without being rebuilt here.
 */
const MenuPdfSection = ({ pdfUrl, pdfName, onChange }: Props) => {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [progress, setProgress] = useState<number | null>(null);
  const [error, setError] = useState("");

  const pick = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    // let the same file be picked again after a remove
    e.target.value = "";
    if (!file) return;

    setError("");
    if (file.type !== "application/pdf") {
      setError("אפשר להעלות קובץ PDF בלבד.");
      return;
    }
    if (file.size > MAX_MB * 1024 * 1024) {
      setError(`הקובץ גדול מדי — עד ${MAX_MB} מגה.`);
      return;
    }

    setProgress(0);
    uploadDocument(file, (percent: number) => setProgress(percent))
      .then((url: string) => {
        onChange({ pdfUrl: url, pdfName: file.name });
        setProgress(null);
      })
      .catch(() => {
        setError("ההעלאה נכשלה. נסה שוב.");
        setProgress(null);
      });
  };

  return (
    <Card data-testid="menu-pdf-section">
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: "10px",
          padding: "11px 14px",
          borderBottom: `1px solid ${v2.line}`,
          background: v2.canvas,
        }}
      >
        <PdfIcon sx={{ fontSize: 18, color: v2.ink2 }} />
        <Box sx={{ fontSize: "14px", fontWeight: 800, color: v2.ink }}>קובץ התפריט</Box>
        <Box sx={{ flex: 1 }} />
        <Box sx={{ fontSize: "11px", color: v2.ink3 }}>המתאמן פותח אותו באפליקציה</Box>
      </Box>

      <Box
        component="input"
        ref={inputRef}
        type="file"
        accept="application/pdf"
        aria-label="קובץ תפריט PDF"
        onChange={pick}
        sx={{ display: "none" }}
      />

      {progress !== null ? (
        <Box sx={{ padding: "18px", display: "flex", alignItems: "center", gap: "12px" }}>
          <CircularProgress size={18} sx={{ color: v2.brand }} />
          <Box sx={{ fontSize: "12.5px", color: v2.ink2 }}>מעלה… {progress}%</Box>
        </Box>
      ) : pdfUrl ? (
        <Box
          data-testid="menu-pdf-file"
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "12px",
            flexWrap: "wrap",
            padding: "14px",
          }}
        >
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: v2.rSm,
              background: v2.overWash,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <PdfIcon sx={{ fontSize: 22, color: v2.over }} />
          </Box>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Box sx={{ fontSize: "13px", fontWeight: 700, color: v2.ink }}>
              {pdfName || "תפריט.pdf"}
            </Box>
            <Box sx={{ fontSize: "11px", color: v2.ink3 }}>הקובץ הועלה ומצורף לתפריט</Box>
          </Box>
          <Box
            component="a"
            href={pdfUrl}
            target="_blank"
            rel="noreferrer"
            aria-label="פתח את קובץ התפריט"
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: "5px",
              fontSize: "12px",
              fontWeight: 600,
              color: v2.ink2,
              textDecoration: "none",
              "&:hover": { color: v2.brand },
            }}
          >
            <OpenIcon sx={{ fontSize: 15 }} />
            פתח
          </Box>
          <TextButton onClick={() => inputRef.current?.click()} sx={{ padding: "4px 11px" }}>
            <UploadIcon sx={{ fontSize: 15 }} />
            החלף קובץ
          </TextButton>
          <TextButton
            tone="danger"
            onClick={() => onChange({ pdfUrl: undefined, pdfName: undefined })}
            sx={{ padding: "4px 11px" }}
          >
            <DeleteIcon sx={{ fontSize: 15 }} />
            הסר
          </TextButton>
        </Box>
      ) : (
        <EmptyState icon={PdfIcon} text="עדיין לא הועלה קובץ. המתאמן יראה את היעדים היומיים בלבד עד שיצורף תפריט.">
          <TextButton tone="green" onClick={() => inputRef.current?.click()}>
            <UploadIcon sx={{ fontSize: 15 }} />
            העלה קובץ PDF
          </TextButton>
        </EmptyState>
      )}

      {error && (
        <Box sx={{ padding: "0 14px 12px", fontSize: "12px", color: v2.over, fontWeight: 600 }}>
          {error}
        </Box>
      )}
    </Card>
  );
};

export default MenuPdfSection;
